import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import StudentMandatoryExamGate from "@/components/StudentMandatoryExamGate";
import {
  House, Users, BookOpen, PencilSimple, ListChecks, ChartBar,
  Files, UploadSimple, Bell, SignOut, GraduationCap, Exam, Archive
} from "@phosphor-icons/react";

const linksByRole = {
  admin: [
    { to: "/admin", label: "Overview", icon: House },
    { to: "/admin/users", label: "Users", icon: Users },
    { to: "/admin/subjects", label: "Subjects & Chapters", icon: BookOpen },
    { to: "/teacher/questions", label: "Question Bank", icon: PencilSimple },
    { to: "/teacher/tests", label: "Tests", icon: Exam },
    { to: "/teacher/results", label: "Results & Analytics", icon: ChartBar },
    { to: "/teacher/assignments", label: "Assignments", icon: Files },
    { to: "/teacher/materials", label: "Study Material", icon: Archive },
    { to: "/teacher/notify", label: "Notifications", icon: Bell },
  ],
  teacher: [
    { to: "/teacher", label: "Overview", icon: House },
    { to: "/teacher/questions", label: "Question Bank", icon: PencilSimple },
    { to: "/teacher/tests", label: "Tests", icon: Exam },
    { to: "/teacher/results", label: "Results & Analytics", icon: ChartBar },
    { to: "/teacher/assignments", label: "Assignments", icon: Files },
    { to: "/teacher/materials", label: "Study Material", icon: Archive },
    { to: "/teacher/notify", label: "Notifications", icon: Bell },
  ],
  student: [
    { to: "/student", label: "Overview", icon: House },
    { to: "/student/tests", label: "Take Tests", icon: Exam },
    { to: "/student/results", label: "My Results", icon: ChartBar },
    { to: "/student/assignments", label: "Assignments", icon: Files },
    { to: "/student/materials", label: "Study Material", icon: Archive },
    { to: "/student/notifications", label: "Notifications", icon: Bell },
  ],
};

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const links = linksByRole[user?.role] || [];

  return (
    <div className="flex min-h-screen bg-bone">
      <aside className="w-64 bg-forest text-ivory flex flex-col sticky top-0 h-screen" data-testid="sidebar">
        <div className="px-6 py-8 border-b border-forest-light">
          <div className="flex items-center gap-2 mb-1">
            <GraduationCap size={22} weight="duotone" className="text-ochre" />
            <span className="overline text-ivory/70">Academy LMS</span>
          </div>
          <h1 className="font-serif text-3xl leading-none">ControlEra</h1>
          <p className="text-xs text-ivory/60 mt-1">est. learning platform</p>
        </div>
        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {links.map((l) => {
            const Icon = l.icon;
            return (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to.endsWith("admin") || l.to.endsWith("teacher") || l.to.endsWith("student")}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 my-0.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-forest-light text-ivory border-l-2 border-ochre"
                      : "text-ivory/75 hover:bg-forest-light hover:text-ivory"
                  }`
                }
              >
                <Icon size={18} weight="regular" />
                <span>{l.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="px-4 py-4 border-t border-forest-light">
          <div className="mb-3">
            <div className="text-xs text-ivory/60 overline">Signed in</div>
            <div className="text-sm text-ivory font-medium truncate">{user?.full_name}</div>
            <div className="text-xs text-ivory/60 capitalize">{user?.role}{user?.class_group ? ` · ${user.class_group}` : ""}</div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="flex items-center gap-2 text-xs text-ivory/80 hover:text-ochre transition-colors"
          >
            <SignOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex min-h-0 flex-1 min-w-0 flex-col">
        <header className="sticky top-0 z-30 shrink-0 bg-bone/85 backdrop-blur-md border-b border-stone px-8 py-4 flex items-center justify-between">
          <div>
            <div className="overline">{user?.role?.toUpperCase()} PANEL</div>
            <h2 className="font-serif text-2xl leading-tight text-forest">Welcome back, {user?.full_name?.split(" ")[0]}</h2>
          </div>
          <button
            onClick={() => navigate(user.role === "student" ? "/student/notifications" : "/teacher/notify")}
            className="btn-ghost flex items-center gap-2"
            data-testid="header-bell"
          >
            <Bell size={18} /> Alerts
          </button>
        </header>
        <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto p-8 animate-fade-up">
          {user?.role === "student" ? (
            <StudentMandatoryExamGate>
              <Outlet />
            </StudentMandatoryExamGate>
          ) : (
            <Outlet />
          )}
        </div>
      </main>
    </div>
  );
}
